import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, type Customer } from "@clevis/shared-types";
import { db } from "../lib/firebase";

export type CustomerRow = Customer & { id: string };

// One live customer list per company for the whole session, shared by every
// picker. A big company (CSG has ~13k customers) downloads it once, the first
// time a picker opens, instead of on every page visit. Office roles only: a
// Field tech can't list customers.
interface Entry {
  rows: CustomerRow[];
  loaded: boolean;
  error: string;
  subscribers: Set<() => void>;
}
const cache = new Map<string, Entry>();

function entryFor(tenantId: string): Entry {
  let e = cache.get(tenantId);
  if (e) return e;
  const entry: Entry = { rows: [], loaded: false, error: "", subscribers: new Set() };
  cache.set(tenantId, entry);
  onSnapshot(
    query(collection(db, paths.tenantCustomers(tenantId)), orderBy("displayName")),
    (snap) => {
      entry.rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Customer) }))
        .filter((c) => !c.archivedAt);
      entry.loaded = true;
      entry.error = "";
      entry.subscribers.forEach((fn) => fn());
    },
    (err) => {
      entry.error = err.message;
      entry.loaded = true;
      entry.subscribers.forEach((fn) => fn());
      cache.delete(tenantId); // let the next picker retry
    },
  );
  e = entry;
  return e;
}

export function useCustomerList(tenantId: string | null, enabled = true): { rows: CustomerRow[]; loaded: boolean; error: string } {
  const [, bump] = useState(0);
  useEffect(() => {
    if (!tenantId || !enabled) return;
    const entry = entryFor(tenantId);
    const fn = () => bump((n) => n + 1);
    entry.subscribers.add(fn);
    fn();
    return () => {
      entry.subscribers.delete(fn);
    };
  }, [tenantId, enabled]);
  const entry = tenantId && enabled ? cache.get(tenantId) : undefined;
  return { rows: entry?.rows ?? [], loaded: entry?.loaded ?? false, error: entry?.error ?? "" };
}
